// First-run superadmin bootstrap.
//
// URL: https://<host>/setup#token=<base64url>
// The token rides in the URL fragment so it never appears in Caddy or brain
// access logs. Single-use — clears on /auth/setup/finish success.

import {
  Alert,
  Button,
  Code,
  Container,
  Image,
  PasswordInput,
  PinInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api, setStoredToken, setStoredUser } from '../api.js';

type Step =
  | 'verifying-token'
  | 'invalid-token'
  | 'enter-details'
  | 'scan-qr'
  | 'done'
  | 'error';

function readTokenFromHash(): string {
  if (typeof window === 'undefined') return '';
  const hash = window.location.hash.replace(/^#/, '');
  const m = /(?:^|&)token=([^&]+)/.exec(hash);
  return m?.[1] ? decodeURIComponent(m[1]) : '';
}

export function SetupPage(): React.JSX.Element {
  const nav = useNavigate();
  const token = useMemo(() => readTokenFromHash(), []);

  const [step, setStep] = useState<Step>('verifying-token');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [userId, setUserId] = useState<string | null>(null);
  const [otpauthQr, setOtpauthQr] = useState<string | null>(null);
  const [totpSecret, setTotpSecret] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [code, setCode] = useState('');

  // Verify token by hitting GET /auth/bootstrap?token=...
  useEffect(() => {
    if (!token) {
      setStep('invalid-token');
      return;
    }
    fetch(`/auth/bootstrap?token=${encodeURIComponent(token)}`)
      .then((r) => setStep(r.ok ? 'enter-details' : 'invalid-token'))
      .catch(() => setStep('invalid-token'));
  }, [token]);

  const onSubmitDetails = async () => {
    setPending(true);
    setErr(null);
    try {
      const r = await api.authSetup({
        token,
        email: email.trim().toLowerCase(),
        display_name: displayName.trim(),
        password,
        ...(phone.trim() ? { phone: phone.trim() } : {}),
      });
      setUserId(r.user_id);
      setOtpauthQr(r.otpauth_qr);
      setTotpSecret(r.totp_secret);
      setRecoveryCodes(r.recovery_codes);
      setStep('scan-qr');
    } catch (e) {
      setErr(humanize(e));
    } finally {
      setPending(false);
    }
  };

  const onConfirmCode = async () => {
    if (!userId) return;
    setPending(true);
    setErr(null);
    try {
      const r = await api.authSetupFinish(userId, code, token);
      setStoredToken(r.access_token);
      setStoredUser(r.user);
      setStep('done');
      window.history.replaceState({}, document.title, '/setup');
      setTimeout(() => nav('/accounts'), 800);
    } catch (e) {
      setErr(humanize(e));
    } finally {
      setPending(false);
    }
  };

  return (
    <Container size="sm" py="xl">
      <Stack>
        <Title order={2}>ai-outlook · first-run setup</Title>
        <Text c="dimmed" size="sm">
          Create the superadmin account. Password + authenticator app are required; SMS is optional
          and enables phone-based 2FA + password reset.
        </Text>

        {err && (
          <Alert color="red" title="Setup failed">
            {err}
          </Alert>
        )}

        {step === 'verifying-token' && (
          <Alert color="blue" title="Verifying token…">
            One moment.
          </Alert>
        )}

        {step === 'invalid-token' && (
          <Alert color="red" title="Token missing or expired">
            This setup link is no longer valid. The bootstrap window is 24 hours, single-use. If you
            missed it, on the VPS run <Code>sudo systemctl restart app@blue.service</Code> and read
            the new URL from <Code>journalctl -u app@blue.service</Code>.
          </Alert>
        )}

        {step === 'enter-details' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void onSubmitDetails();
            }}
          >
            <Stack>
              <TextInput
                label="Email"
                description="Used as your sign-in identifier."
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                type="email"
                required
                autoComplete="username"
              />
              <TextInput
                label="Display name"
                value={displayName}
                onChange={(e) => setDisplayName(e.currentTarget.value)}
                required
                autoComplete="name"
              />
              <PasswordInput
                label="Password"
                description="Minimum 12 characters. Stored as argon2id hash."
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                required
                autoComplete="new-password"
              />
              <TextInput
                label="Mobile number (optional)"
                description="E.164 format e.g. +96170123456. Used for SMS 2FA + password reset via BroadNet SMS."
                placeholder="+96170123456"
                value={phone}
                onChange={(e) => setPhone(e.currentTarget.value)}
                type="tel"
                autoComplete="tel"
              />
              <Button
                type="submit"
                loading={pending}
                disabled={
                  !email.trim() || !displayName.trim() || password.length < 12
                }
              >
                Create account
              </Button>
            </Stack>
          </form>
        )}

        {step === 'scan-qr' && otpauthQr && (
          <Stack>
            <Title order={4}>Scan with your authenticator</Title>
            <Text size="sm" c="dimmed">
              Open Google Authenticator, 1Password, Bitwarden, or any TOTP app and scan this QR.
              Then type the 6-digit code shown to confirm.
            </Text>
            <Image
              src={otpauthQr}
              alt="otpauth QR code"
              w={220}
              h={220}
              fit="contain"
              radius="sm"
              style={{ background: '#fff', padding: 8 }}
            />
            {totpSecret && (
              <Text size="xs" c="dimmed">
                Manual entry secret: <Code>{totpSecret}</Code>
              </Text>
            )}

            {recoveryCodes && (
              <Alert color="yellow" title="Save your recovery codes (one-time view)">
                <Stack gap={2}>
                  <Text size="xs">
                    Each code is single-use. If you lose your authenticator, one of these is the
                    only way back in besides SMS reset (if configured).
                  </Text>
                  <Code block>{recoveryCodes.join('\n')}</Code>
                </Stack>
              </Alert>
            )}

            <Title order={5}>Confirm</Title>
            <PinInput
              length={6}
              type="number"
              inputMode="numeric"
              value={code}
              onChange={setCode}
              aria-label="totp code"
              oneTimeCode
            />
            <Button
              onClick={() => void onConfirmCode()}
              loading={pending}
              disabled={code.length !== 6}
            >
              Confirm and sign in
            </Button>
          </Stack>
        )}

        {step === 'done' && (
          <Alert color="teal" title="Setup complete">
            Superadmin created. Redirecting to the dashboard…
          </Alert>
        )}
      </Stack>
    </Container>
  );
}

function humanize(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.message === 'weak_password') return 'Password too weak: minimum 12 characters, not in obvious-weak list.';
    if (e.message === 'no_active_bootstrap') return 'Setup token has been used or expired.';
    if (e.message === 'invalid_code') return 'Authenticator code is wrong — try again with the next 30s code.';
    return e.message;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}
